import { useState } from 'react';
import { FileText, Play, Mic, ChevronRight, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VoiceMemoStatusBadge } from './voice-memo-status-badge';
import { VoiceMemoPlayer } from './voice-memo-player';
import { TitleEditModal } from '@/components/ui/title-edit-modal';
import { updateVoiceMemoTitle, fetchWithAuth } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import type { VoiceMemo } from '@/lib/api';

interface VoiceMemoListItemProps {
  memo: VoiceMemo;
  onProcess?: (filename: string) => void;
  onViewTranscript?: (memo: VoiceMemo) => void;
  onReviewAndFile?: (memo: VoiceMemo) => void;
  isSelected?: boolean;
}

export function VoiceMemoListItem({ memo, onProcess, onViewTranscript, onReviewAndFile, isSelected = false }: VoiceMemoListItemProps) {
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleSaveTitle = async (title: string, summary?: string) => {
    await updateVoiceMemoTitle(memo.id, title, summary);
    queryClient.invalidateQueries({ queryKey: ['voice-memos'] });
  };

  const handleGenerateAI = async (mode: 'both' | 'title-only' | 'summary-only' = 'both') => {
    const response = await fetchWithAuth(`/api/voice-memos/${memo.id}/generate-title`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mode }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate');
    }

    const data = await response.json();
    return { title: data.title, summary: data.summary };
  };

  return (
    <div className={`group px-3 py-2.5 border-b border-border transition-all duration-500 ease-in-out ${
      isSelected
        ? 'border-l-2 border-l-primary/50 bg-primary/10 hover:bg-primary/15'
        : 'hover:bg-muted/50'
    }`}>
      {/* First Row: Title, Audio Player, Badge & Action Button */}
      <div className="flex items-center gap-4 mb-2">
        <Mic className="h-5 w-5 text-purple-500 flex-shrink-0" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4
              className="font-medium text-sm truncate"
              title={memo.title || memo.filename}
            >
              {memo.title || memo.filename}
            </h4>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
              onClick={() => setIsTitleModalOpen(true)}
              title={memo.title ? 'Edit title' : 'Add title'}
            >
              <Edit2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Audio Player - responsive width */}
          <div className="w-48 xl:w-64">
            <VoiceMemoPlayer filename={memo.filename} duration={memo.duration} />
          </div>

          {/* Badge & Action Button Column */}
          <div className="w-32 flex-shrink-0 flex flex-col items-end gap-1.5">
            {/* Status Badge */}
            <VoiceMemoStatusBadge status={memo.status} />

            {/* Action Button */}
            {/* Show transcribe button if no transcript exists, regardless of status */}
            {!memo.transcript?.path && onProcess && (
              <Button
                size="sm"
                variant="default"
                onClick={() => onProcess(memo.filename)}
                className="w-full"
              >
                <Play className="w-4 h-4 mr-2" />
                Transcribe
              </Button>
            )}

            {/* Show view transcript/analysis buttons only if transcript exists */}
            {memo.transcript?.path && memo.status === 'transcribed' && (
              <button
                className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded-md hover:bg-muted/50"
                onClick={() => onViewTranscript?.(memo)}
              >
                <span>View transcript</span>
                <ChevronRight className="h-3 w-3 ml-2" aria-hidden="true" />
              </button>
            )}

            {memo.transcript?.path && memo.status === 'analyzed' && (
              <button
                className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5 rounded-md hover:bg-muted/50"
                onClick={() => onReviewAndFile?.(memo)}
              >
                <span>View analysis</span>
                <ChevronRight className="h-3 w-3 ml-2" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Second Row: Metadata */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground pl-9">
        {memo.title && (
          <>
            <span className="truncate" title={memo.filename}>{memo.filename}</span>
            <span>•</span>
          </>
        )}
        <span>{memo.date}</span>
        <span>•</span>
        <span>{memo.duration}</span>
        {memo.fileSize && (
          <>
            <span>•</span>
            <span>{memo.fileSize}</span>
          </>
        )}
        {memo.transcript?.wordCount && (
          <>
            <span>•</span>
            <span>{memo.transcript.wordCount} words</span>
          </>
        )}
      </div>

      <TitleEditModal
        open={isTitleModalOpen}
        onOpenChange={setIsTitleModalOpen}
        title={memo.title || ''}
        subtitle={memo.filename}
        summary={memo.summary || ''}
        showSummary={true}
        onSave={handleSaveTitle}
        onGenerateAI={memo.transcript?.path ? handleGenerateAI : undefined}
        placeholder="Enter a title for this voice memo"
        summaryPlaceholder="Enter a brief summary (optional)"
      />
    </div>
  );
}

